import type { Tick, TickLabelItem } from "./types";

// ─── Core Helpers ─────────────────────────────────────────────────────────────

/** Converts a value to a percentage position within a viewport range. */
export function valueToPercent(val: number, viewStart: number, viewEnd: number): number {
    if (viewEnd === viewStart) return 0;
    return ((val - viewStart) / (viewEnd - viewStart)) * 100;
}

// ─── Keyboard Step ────────────────────────────────────────────────────────────

/** Returns the step size for PageUp/PageDown keyboard navigation. */
export function getKeyboardStep(span: number): number {
    if (span <= 25) return 1;
    if (span <= 100) return 5;
    return 10;
}

// ─── Tick Generation ──────────────────────────────────────────────────────────
//
// Ticks are the small dots rendered on the track. Their density adapts to the
// visible range so the track never looks too sparse or too crowded.
//
// Tier breakdown:
//   ≤ 10 units  → dot every 1 unit
//   ≤ 25 units  → dot every 5 units
//   ≤ 50 units  → dot every 5 units
//   ≤ 100 units → dot every 10 units
//   > 100 units → dot every 25 units

/** Returns the interval between tick dots for a given span. */
function getDotStep(span: number): number {
    if (span <= 10) return 1;
    if (span <= 50) return 5;
    if (span <= 100) return 10;
    return 25;
}

/**
 * Returns the interval for "major" ticks (used by label builder to determine
 * which ticks align with pipe positions).
 */
function getPipeStep(span: number): number {
    if (span <= 10) return 1;
    if (span <= 25) return 5;
    if (span <= 50) return 10;
    if (span <= 100) return 25;
    return 50;
}

/** Generates tick dots for the given viewport range. */
export function generateTicks(viewStart: number, viewEnd: number): Tick[] {
    if (viewEnd <= viewStart) {
        return [{ value: viewStart, percent: 0, isMajor: true }];
    }

    const span = viewEnd - viewStart;
    const dotStep = getDotStep(span);
    const pipeStep = getPipeStep(span);
    const ticks: Tick[] = [];

    // Start from the first dotStep-aligned value within the viewport
    const first = Math.ceil(viewStart / dotStep) * dotStep;

    for (let val = first; val <= viewEnd; val += dotStep) {
        ticks.push({
            value: val,
            percent: valueToPercent(val, viewStart, viewEnd),
            isMajor: val % pipeStep === 0,
        });
    }

    return ticks;
}

// ─── Label Builder ────────────────────────────────────────────────────────────
//
// Labels appear above the track and consist of value numbers and pipe "|" markers.
// The density and style adapts to the viewport span:
//
//   ≤ 10 units  → 2 value labels (start + end), pipes at every 1
//   ≤ 25 units  → 3 value labels (start + middle + end), pipes at every 5
//   ≤ 50 units  → value labels every 25, pipes every 10 (step-aligned only)
//   ≤ 100 units → value labels every 50, pipes every 25 (step-aligned only)
//   > 100 units → value labels every 100, pipes every 50 (step-aligned only)
//
// For ≤50 / ≤100 / >100 tiers, boundary labels (viewStart/viewEnd) are omitted
// to avoid overlap — the knob tooltips show exact values instead.

/** Minimum percent gap required between a pipe and its neighboring value label. */
const PIPE_OVERLAP_THRESHOLD = 3;

/**
 * Builds labels for the ≤50 / ≤100 / >100 tiers using step-aligned positions.
 * No boundary labels are added — only clean multiples of labelStep and pipeStep.
 */
function buildSteppedLabels(viewStart: number, viewEnd: number, labelStep: number, pipeStep: number): TickLabelItem[] {
    // Value labels at every labelStep-aligned value
    const valueLabels: TickLabelItem[] = [];
    const firstLabel = Math.ceil(viewStart / labelStep) * labelStep;
    for (let val = firstLabel; val <= viewEnd; val += labelStep) {
        valueLabels.push({
            type: "value",
            value: val,
            percent: valueToPercent(val, viewStart, viewEnd),
        });
    }

    // Pipe labels at every pipeStep-aligned value (excluding value label positions)
    const valueSet = new Set(valueLabels.map((l) => l.value));
    const pipeLabels: TickLabelItem[] = [];
    const firstPipe = Math.ceil(viewStart / pipeStep) * pipeStep;
    for (let val = firstPipe; val <= viewEnd; val += pipeStep) {
        if (!valueSet.has(val)) {
            pipeLabels.push({
                type: "pipe",
                value: val,
                percent: valueToPercent(val, viewStart, viewEnd),
            });
        }
    }

    // Merge, sort, and filter pipes that are too close to value labels
    const all = [...valueLabels, ...pipeLabels].sort((a, b) => a.percent - b.percent);
    const result: TickLabelItem[] = [];

    for (let i = 0; i < all.length; i++) {
        const item = all[i] as TickLabelItem;

        if (item.type === "pipe") {
            const prev = result[result.length - 1];
            const next = all[i + 1];
            const gapToPrev = prev ? item.percent - prev.percent : Infinity;
            const gapToNext = next ? next.percent - item.percent : Infinity;

            if (gapToPrev < PIPE_OVERLAP_THRESHOLD || gapToNext < PIPE_OVERLAP_THRESHOLD) {
                continue;
            }
        }

        result.push(item);
    }

    return result;
}

/**
 * Builds the full set of tick labels (values + pipes) for the given viewport.
 * Adapts density based on the visible span to keep labels readable.
 */
export function buildTickLabels(_ticks: Tick[], viewStart: number, viewEnd: number): TickLabelItem[] {
    if (viewEnd <= viewStart) return [];

    const span = viewEnd - viewStart;

    // ≤ 10 units: start and end value labels with a pipe at every unit between them
    if (span <= 10) {
        const result: TickLabelItem[] = [];
        result.push({ type: "value", value: viewStart, percent: 0 });

        for (let val = viewStart + 1; val < viewEnd; val++) {
            result.push({
                type: "pipe",
                value: val,
                percent: valueToPercent(val, viewStart, viewEnd),
            });
        }

        result.push({ type: "value", value: viewEnd, percent: 100 });
        return result;
    }

    // ≤ 25 units: start, middle, and end value labels with pipes at every 5
    if (span <= 25) {
        const result: TickLabelItem[] = [];
        const mid = Math.round((viewStart + viewEnd) / 2);

        result.push({ type: "value", value: viewStart, percent: 0 });

        // Pipes at every 5-aligned value (excluding label positions)
        const dotStep = 5;
        const firstDot = Math.ceil(viewStart / dotStep) * dotStep;
        for (let val = firstDot; val <= viewEnd; val += dotStep) {
            if (val === viewStart || val === viewEnd || val === mid) continue;
            result.push({
                type: "pipe",
                value: val,
                percent: valueToPercent(val, viewStart, viewEnd),
            });
        }

        result.push({ type: "value", value: mid, percent: valueToPercent(mid, viewStart, viewEnd) });
        result.push({ type: "value", value: viewEnd, percent: 100 });

        result.sort((a, b) => a.percent - b.percent);
        return result;
    }

    // ≤ 50 units: value labels every 25, pipes every 10
    if (span <= 50) {
        return buildSteppedLabels(viewStart, viewEnd, 25, 10);
    }

    // ≤ 100 units: value labels every 50, pipes every 25
    if (span <= 100) {
        return buildSteppedLabels(viewStart, viewEnd, 50, 25);
    }

    // > 100 units: value labels every 100, pipes every 50
    return buildSteppedLabels(viewStart, viewEnd, 100, 50);
}
